// @flow
import _ from 'lodash'
import React, { Component } from 'react'
import ReactSelect, { components } from 'react-select'
import { linkColor } from './colors'
import type { OptionType, GroupedOptionType } from '../types'
import { innerOptions, flatInnerOptions } from './Select/optionTransformations'

export type InnerOption = {|
  label: string,
  value?: any,
  options?: Array<InnerOption>
|}

type Props = {|
  value: any,
  options: Array<OptionType>,
  groupedOptions?: Array<GroupedOptionType>,
  placeholder?: string,
  disabled?: boolean,
  htmlProperties?: Object,
  onChange?: (any) => void,
  isClearable?: boolean,
  noResultsText?: ?string
|}

export const OrtSelectStyles = {
  option: (provided: Object, state: Object) => {
    let backgroundColor = provided.backgroundColor
    let color = state.color
    if (state.isFocused) backgroundColor = '#edf4ff'
    if (state.isSelected) backgroundColor = '#d2e9f8'
    if (state.isFocused || state.isSelected) color = 'black'
    if (state.isDisabled) color = 'gray'
    return {
      ...provided,
      fontSize: '14px',
      backgroundColor,
      color
    }
  },
  singleValue: (provided: Object) => {
    return {
      ...provided,
      fontSize: '14px'
    }
  },
  dropdownIndicator: (provided: Object, state: Object) => {
    const extra = state.selectProps.menuIsOpen ? { transform: 'rotate(-180deg)' } : {}
    return {
      ...provided,
      padding: '5px',
      ...extra
    }
  },
  container: (provided: Object) => {
    return {
      ...provided
    }
  },
  control: (provided: Object, state: Object) => {
    let extraStyles = {}
    if (state.selectProps.menuIsOpen) {
      extraStyles = {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0
      }
    }
    return {
      ...provided,
      boxShadow: 'none',
      minHeight: '32px',
      ...extraStyles
    }
  },
  indicatorSeparator: (provided: Object) => {
    return {
      ...provided,
      display: 'none'
    }
  },
  menu: (provided: Object) => {
    return {
      ...provided,
      marginTop: 0,
      borderRadius: 0
    }
  },
  menuList: (provided: Object) => {
    return {
      ...provided,
      paddingTop: 0,
      paddingBottom: 0
    }
  },
  clearIndicator: (provided: Object) => {
    return {
      ...provided,
      padding: '5px'
    }
  },
  multiValue: (provided: Object) => {
    return {
      ...provided,
      color: linkColor,
      backgroundColor: 'white',
      border: `solid 1px ${linkColor}`
    }
  },
  multiValueLabel: (provided: Object) => {
    return {
      ...provided,
      color: linkColor
    }
  },
  multiValueRemove: (provided: Object) => {
    return {
      ...provided,
      ':hover': {
        ...provided[':hover'],
        backgroundColor: 'initial'
      }
    }
  }
}

const t = {
  are_you_sure: 'Вы уверены, что хотите удалить все выбранные значения?'
}

const Option = (props: Object) => <components.Option {...props} className="t-ReactSelect__Option" />

const ValueContainer = (props: Object) => (
  <components.ValueContainer {...props} className="t-ReactSelect__Value" />
)

const ClearIndicator = (props: Object) => {
  const { innerProps, selectProps } = props

  return (
    components.ClearIndicator && (
      <components.ClearIndicator
        {...props}
        className="t-ReactSelect__ClearAll"
        innerProps={{
          ...innerProps,
          onMouseDown: (event) => {
            if (selectProps.requireConfirmOnClearAll && !window.confirm(t.are_you_sure)) return
            innerProps.onMouseDown(event)
          }
        }}
      />
    )
  )
}

export const OrtSelectComponents = { Option, ValueContainer, ClearIndicator }

export default class Select extends Component<Props> {
  static optionsFromTuples(tuples: Array<[string, any]>): Array<OptionType> {
    return _.map(tuples, ([name, id]) => ({ id, name }))
  }

  render() {
    return (
      <ReactSelect
        value={this.value()}
        options={this.innerOptionsWithGroups()}
        clearable={this.props.isClearable || false}
        isDisabled={this.props.disabled}
        placeholder={this.props.placeholder}
        noOptionsMessage={() => this.props.noResultsText}
        styles={OrtSelectStyles}
        components={OrtSelectComponents}
        removeSelected
        onChange={(option) => this.changed(option)}
        {...this.props.htmlProperties}
      />
    )
  }

  innerOptionsWithGroups(): Array<InnerOption> {
    return innerOptions(this.props.options, this.props.groupedOptions)
  }

  changed(option: InnerOption) {
    this.props.onChange && this.props.onChange(option && option.value)
  }

  value(): ?InnerOption {
    return _.find(
      flatInnerOptions(this.props.options, this.props.groupedOptions),
      (option) => option.value === this.props.value
    )
  }
}
